/**
 * D1a Task 12: company AI settings and the staff platform AI settings.
 * Backend: app/django-core/apps/ai/ (views.py, urls.py); contract in task-9-report.md.
 *
 * - `connection/` GET is open to any user of the tenant. Admins get `api_key: {set, last4}`
 *   and `base_url`; everyone else gets `api_key: {set}` and no `base_url`.
 * - `connection/` PUT is partial. Send `api_key` only when the user typed a new key
 *   ("" removes it); never send the GET object back.
 * - `connection/test/` is throttled per company (429).
 * - `platform/` is is_staff only (IsAdminUser).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';

const BASE = '/api/v1/ai';

export const aiApi = createApi({
  reducerPath: 'aiApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['AIConnection', 'AINotice', 'AIUsage', 'PlatformAI'],
  endpoints: (builder) => ({
    getAiConnection: builder.query({
      query: () => ({ url: `${BASE}/connection/` }),
      providesTags: ['AIConnection'],
    }),
    updateAiConnection: builder.mutation({
      query: (body) => ({ url: `${BASE}/connection/`, method: 'PUT', body }),
      invalidatesTags: ['AIConnection'],
    }),
    testAiConnection: builder.mutation({
      query: () => ({ url: `${BASE}/connection/test/`, method: 'POST', body: {} }),
      invalidatesTags: ['AIUsage'],
    }),
    getAiNotice: builder.query({
      query: () => ({ url: `${BASE}/notice/` }),
      providesTags: ['AINotice'],
    }),
    acceptAiNotice: builder.mutation({
      query: () => ({ url: `${BASE}/notice/accept/`, method: 'POST', body: {} }),
      invalidatesTags: ['AINotice'],
    }),
    getAiUsage: builder.query({
      query: () => ({ url: `${BASE}/usage/` }),
      providesTags: ['AIUsage'],
    }),
    getPlatformAi: builder.query({
      query: () => ({ url: `${BASE}/platform/` }),
      providesTags: ['PlatformAI'],
    }),
    updatePlatformAi: builder.mutation({
      query: (body) => ({ url: `${BASE}/platform/`, method: 'PUT', body }),
      // A new notice version changes what every company must accept.
      invalidatesTags: ['PlatformAI', 'AINotice', 'AIConnection'],
    }),
  }),
});

export const {
  useGetAiConnectionQuery,
  useUpdateAiConnectionMutation,
  useTestAiConnectionMutation,
  useGetAiNoticeQuery,
  useAcceptAiNoticeMutation,
  useGetAiUsageQuery,
  useGetPlatformAiQuery,
  useUpdatePlatformAiMutation,
} = aiApi;
